import compact from 'lodash/compact';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShelterEquipmentDetailsFragment } from '../../../../../generated/graphql';
import { StopWithDetails } from '../../../../../types';
import { showSuccessToast } from '../../../../../utils';
import { AddNewButton } from '../../../../common/Buttons';
import { submitFormByRef } from '../../../../common/Forms';
import {
  InfoContainer,
  useInfoContainerControls,
} from '../../../../common/InfoContainer';
import { EmptyListHeaderButtons } from '../Common/EmptyListHeaderButtons';
import { stopInfoContainerColors } from '../Common/stopInfoContainerColors';
import { SheltersFormState, mapShelterDataToFormState } from './schema';
import { SheltersForm, SheltersFormRef } from './SheltersForm';
import { SheltersViewList } from './SheltersViewList';
import { useEditStopShelters } from './useEditStopShelters';

const testIds = {
  addShelter: 'SheltersSection::addShelter',
};

type SheltersSectionProps = {
  readonly stop: StopWithDetails;
};

function getShelterFormDefaultValues(
  shelters: ReadonlyArray<ShelterEquipmentDetailsFragment>,
) {
  if (shelters.length === 0) {
    return { shelters: [mapShelterDataToFormState({})] };
  }

  return { shelters: shelters.map(mapShelterDataToFormState) };
}

export const SheltersSection: FC<SheltersSectionProps> = ({ stop }) => {
  const { t } = useTranslation();

  const { saveStopPlaceShelters, defaultErrorHandler } = useEditStopShelters();

  const shelters = compact(stop.quay?.placeEquipments?.shelterEquipment);
  const [shelterCount, setShelterCount] = useState(shelters.length);

  const formRef = useRef<HTMLFormElement>(null);
  const sheltersFormRef = useRef<SheltersFormRef>(null);

  const infoContainerControls = useInfoContainerControls({
    isExpandable: true,
    isEditable: true,
    expandedByDefault: shelterCount > 0,
    onSave: () => submitFormByRef(formRef),
  });
  const { isInEditMode, setIsInEditMode, setIsExpanded } =
    infoContainerControls;

  useEffect(() => {
    if (!isInEditMode) {
      setShelterCount(shelters.length);
    }
  }, [isInEditMode, shelters]);

  const onSubmit = async (state: SheltersFormState) => {
    try {
      await saveStopPlaceShelters({ state, stop });

      showSuccessToast(t(($) => $.stops.editSuccess));
      infoContainerControls.setIsInEditMode(false);
    } catch (err) {
      defaultErrorHandler(err as Error);
    }
  };

  const shelterFormDefaultValues = getShelterFormDefaultValues(shelters);

  const editAndAddShelter = () => {
    setIsInEditMode(true);
    setIsExpanded(true);
    setShelterCount(shelterFormDefaultValues.shelters.length);
  };

  const handleAddNewShelter = () => {
    if (sheltersFormRef.current) {
      sheltersFormRef.current.addNewShelter();
    }
  };

  const showAddNewShelterHeader = !isInEditMode && !shelters.length;
  const sectionTitle = shelterCount
    ? t(($) => $.stopDetails.shelters.title, {
        count: shelterCount,
      })
    : t(($) => $.stopDetails.shelters.titleNoShelters);

  return (
    <InfoContainer
      colors={stopInfoContainerColors}
      controls={infoContainerControls}
      headerButtons={
        showAddNewShelterHeader ? (
          <EmptyListHeaderButtons
            addNewItemText={t(($) => $.stopDetails.shelters.addShelter)}
            onAddNewItem={editAndAddShelter}
            testIdPrefix="SheltersSection"
          />
        ) : undefined
      }
      title={sectionTitle}
      ariaLabel={sectionTitle}
      testIdPrefix="SheltersSection"
    >
      {isInEditMode ? (
        <SheltersForm
          defaultValues={shelterFormDefaultValues}
          formRef={formRef}
          ref={sheltersFormRef}
          onShelterCountChanged={setShelterCount}
          onSubmit={onSubmit}
          onCancel={() => infoContainerControls.setIsInEditMode(false)}
          testIdPrefix="SheltersSection"
          addNewButton={
            <AddNewButton
              onClick={handleAddNewShelter}
              label={
                <span className="text-hsl-dark-80 hover:text-tweaked-brand">
                  {t(($) => $.stopDetails.shelters.addNewShelter)}
                </span>
              }
              testId={testIds.addShelter}
            />
          }
        />
      ) : (
        <SheltersViewList shelters={shelters} />
      )}
    </InfoContainer>
  );
};
